import { createOrUpdateAttendance } from './attendanceApi';
import { apiClient } from '.';

// 강의 API 관련 타입 정의
export interface Lecture {
  id: number;
  title: string;
  caption: string;
  thumbnail_url: string | null;
  lecturer_info?: {
    id: number;
    name: string;
    introduction: string;
    image_url: string | null;
  } | null;
}

export interface Chapter {
  id: number;
  title: string;
  startTime: number;
  endTime: number;
  timecode: number; // StreamingChapters에서 사용하는 속성
  uri: string; // StreamingChapters에서 사용하는 속성
}

export interface LectureListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    content: Lecture[];
    pageable: {
      pageNumber: number;
      pageSize: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    totalElements: number;
    totalPages: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface LectureListParams {
  page?: number;
  size?: number;
}

// 시간 포맷팅 유틸리티 함수
export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
};

// 강의 목록 조회 API
export const fetchLectures = async (params: LectureListParams = {}): Promise<LectureListResponse> => {
  const { page = 0, size = 3 } = params;
  
  const url = `/api/lectures/info/list?page=${page}&size=${size}`;

  try {
    console.log(`[lectureApi] 강의 목록 조회 시작 - page: ${page}, size: ${size}`);
    console.log(`[lectureApi] 요청 URL: ${url}`);
    
    const { data } = await apiClient.get<LectureListResponse>(url, { withCredentials: true });
    console.log(`[lectureApi] 응답 데이터:`, data);
    console.log(`[lectureApi] 강의 목록 조회 성공 (${data.data.content.length}개)`);
    
    return data;
  } catch (error) {
    console.error(`[lectureApi] 강의 목록 조회 실패:`, error);
    throw error;
  }
};

// 강의 챕터 조회 API
export const getLectureChapters = async (lectureId: number): Promise<{ data: Chapter[] }> => {
  const url = `/api/lectures/info/${lectureId}/chapters`;

  try {
    console.log('강의 챕터 조회:', lectureId);
    
    const { data } = await apiClient.get<{ code: number; data: Chapter[]; message: string }>(
      url,
      { withCredentials: true }
    );
    console.log('Chapters API response:', data);
    
    // API 응답이 성공적이면 데이터 반환
    if (data.code === 200) {
      return { data: data.data };
    } else {
      throw new Error(data.message || '챕터 데이터를 불러오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('강의 챕터 조회 실패:', error);
    
    // 에러 발생 시 더미 데이터 반환 (개발 중에만 사용)
    console.log('API 호출 실패, 더미 데이터 반환');
    return {
      data: [
        { 
          id: 1, 
          title: `강의 ${lectureId} - 소개`, 
          startTime: 0, 
          endTime: 300, 
          timecode: 0, 
          uri: 'chapter-1' 
        },
        { 
          id: 2, 
          title: `강의 ${lectureId} - 본론`, 
          startTime: 300, 
          endTime: 1200, 
          timecode: 300, 
          uri: 'chapter-2' 
        },
        { 
          id: 3, 
          title: `강의 ${lectureId} - 마무리`, 
          startTime: 1200, 
          endTime: 1500, 
          timecode: 1200, 
          uri: 'chapter-3' 
        }
      ]
    };
  }
};

// 강의 시청 기록 및 출석 처리 API
export const recordLectureWatch = async (lectureId: number, watchedDuration: number): Promise<{ success: boolean }> => {
  try {
    // 1초 이상 시청했으면 출석 처리
    if (watchedDuration >= 1) {
      const attendanceResult = await createOrUpdateAttendance({
        lectureId,
        watchedDuration
      });
      
      if (attendanceResult.success) {
        console.log(`강의 ${lectureId} 시청으로 출석 처리됨 (${watchedDuration}초)`);
      }
      
      return attendanceResult;
    }
    
    return { success: true }; // 1초 미만은 출석 처리 안함
  } catch (error) {
    console.error('강의 시청 기록 실패:', error);
    return { success: false };
  }
};

// 초(seconds)를 h-m-s 형식으로 변환하는 함수
export const formatProgressTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  return `${hours}h${minutes}m${remainingSeconds}s`;
};

// h-m-s 형식을 초(seconds)로 변환하는 함수
export const parseProgressTime = (progressTime: string): number => {
  const regex = /(\d+)h(\d+)m(\d+)s/;
  const match = progressTime.match(regex);
  
  if (!match) return 0;
  
  const hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const seconds = parseInt(match[3]);
  
  return hours * 3600 + minutes * 60 + seconds;
};

// Q&A 게시글 타입 정의
export interface QnaPost {
  post_id: number;
  title: string;
  content: string;
  post_category: string;
  reply_cnt: number;
  view_cnt: number;
  writer_name: string;
  created_at: string;
  lecture_name?: string;
  chapter?: number;
  study_name?: string;
  pinned: boolean;
}

// 게시글 상세 정보 타입 정의
export interface PostDetail {
  post_category: string;
  post_title: string;
  post_content: string;
  writer_name: string;
  created_at: string;
  can_update: boolean;
  reply_cnt: number;
  view_cnt: number;
  lecture_title?: string;
  lecture_chapter?: string;
  study_name?: string;
  replies: Reply[];
}

export interface Reply {
  reply_id: number;
  content: string;
  writer_name: string;
  reply_created_at: string;
  images?: string[];
}

export interface PostDetailResponse {
  code: number;
  status: string;
  message: string;
  data: PostDetail;
}

export interface QnaListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: QnaPost[];
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 강의별 Q&A 목록 조회 API
export const getLectureQnaPosts = async (
  lectureId: number, 
  page: number = 0, 
  size: number = 10,
  chapter?: number
): Promise<QnaListResponse> => {
  
  // 쿼리 파라미터 구성
  const params = new URLSearchParams({
    board: 'lecture',
    lectureId: lectureId.toString(),
    page: page.toString(),
    size: size.toString()
  });
  
  // 챕터가 지정된 경우 추가
  if (chapter !== undefined) {
    params.append('chapter', chapter.toString());
  }
  
  const url = `/api/posts/list?${params.toString()}`;

  try {
    console.log('Q&A 목록 조회:', { lectureId, page, size, chapter, url });
    
    const { data } = await apiClient.get<QnaListResponse>(url, { withCredentials: true });
    console.log('QnA API response:', data);
    
    return data;
  } catch (error) {
    console.error('Q&A 목록 조회 실패:', error);
    throw error;
  }
};

// 게시글 상세조회 API
export const getPostDetail = async (postId: number): Promise<PostDetailResponse> => {
  const url = `/api/posts/${postId}`;

  try {
    console.log('게시글 상세조회:', postId);
    
    const { data } = await apiClient.get<PostDetailResponse>(url, { withCredentials: true });
    console.log('Post detail API response:', data);
    
    return data;
  } catch (error) {
    console.error('게시글 상세조회 실패:', error);
    throw error;
  }
};

// 강의 수강 진행률 조회 API
export const getLectureProgress = async (lectureId: number) => {
  const url = `/api/me/progress?lectureId=${lectureId}`;

  try {
    console.log('강의 진행률 조회:', lectureId);
    
    const { data } = await apiClient.get(url, { withCredentials: true });
    console.log('Progress API response:', data);
    
    return data;
  } catch (error) {
    console.error('강의 진행률 조회 실패:', error);
    throw error;
  }
};

// 강의 수강 진행률 업데이트 API
export const updateLectureProgress = async (lectureId: number, progressSeconds: number) => {
  const url = `/api/enrollments/${lectureId}/progress`;
  
  const progressTime = formatProgressTime(progressSeconds);

  try {
    console.log(`강의 진행률 업데이트: ${lectureId}, progress: ${progressTime} (${progressSeconds}초)`);
    
    const { data } = await apiClient.put(
      url,
      { progress: progressTime },
      { withCredentials: true }
    );
    console.log('Progress update response:', data);
    
    return data;
  } catch (error) {
    console.error('강의 진행률 업데이트 실패:', error);
    throw error;
  }
};

// 설문 질문 조회
export const getSurveyQuestion = async (questionId: number) => {
  const response = await apiClient.get(`/api/lecture-survey/${questionId}`);
  return response.data;
};

// 모든 설문 질문 조회 (1-20번)
export const getAllSurveyQuestions = async () => {
  const questions = [];
  
  for (let i = 1; i <= 20; i++) {
    try {
      const response = await getSurveyQuestion(i);
      if (response.code === 200 && response.data.active) {
        questions.push({
          id: response.data.id,
          question: response.data.question,
          category_name: response.data.category_name,
          selection_count: response.data.selection_count
        });
      }
    } catch (error) {
      console.warn(`설문 질문 ${i}번 조회 실패:`, error);
    }
  }
  
  return questions;
};

// 강의 설문 제출
export const submitLectureSurvey = async (lectureId: number, responses: { [key: string]: number | string }) => {
  try {
    const answers = Object.entries(responses).map(([key, value]) => {
      const surveyId = parseInt(key.replace('question_', ''));
      return {
        surveyId: surveyId,
        answer: value.toString()
      };
    });

    const requestData = {
      lectureId: parseInt(lectureId.toString()),
      answers: answers
    };

    console.log('설문 제출 요청:', requestData);

    const response = await apiClient.post('/api/lecture-survey/answers', requestData);
    return response.data;
  } catch (error) {
    console.error('설문 제출 실패:', error);
    throw error;
  }
}; 
